import { useState } from 'react';
import { Btn, Icon } from '../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../forms/ui/tokens';
import { hostedSurveyUrl } from './hostedUrl';

/** Feather "link" glyph (atoms/ICONS has no link icon). */
const LINK_ICON = [
  'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
  'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
] as const;
const CHECK_ICON = 'M5 12l5 5L20 7';

/**
 * Copies the public hosted survey link to the clipboard. Use only for
 * published, anonymous surveys (identified surveys have no single shareable
 * link; an unpublished slug 404s). `variant="icon"` is the compact form for
 * table rows; `variant="button"` is the labelled form for toolbars.
 */
export function CopyLinkButton({ slug, variant = 'button' }: { slug: string; variant?: 'button' | 'icon' }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(hostedSurveyUrl(slug));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Link copied' : 'Copy survey link'}
        aria-label="Copy survey link"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 7,
          border: `1px solid ${copied ? C.teal : C.line}`,
          background: copied ? C.contactBg : '#fff',
          cursor: 'pointer',
          fontFamily: FORM_UI_FONT,
        }}
      >
        <Icon d={copied ? CHECK_ICON : LINK_ICON} size={14} color={copied ? C.tealDark : C.sub} sw={copied ? 2.4 : 1.8} />
      </button>
    );
  }

  return (
    <Btn kind="ghost" onClick={() => void copy()}>
      <Icon d={copied ? CHECK_ICON : LINK_ICON} size={15} color={copied ? C.tealDark : C.ink} sw={copied ? 2.4 : 1.8} />
      {copied ? 'Copied' : 'Copy link'}
    </Btn>
  );
}
